import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, throwError, catchError, map, switchMap, tap } from 'rxjs';
import {
  persistVaultAuthMeTenants,
  persistVaultTenantFromTenantsPayload,
  readStoredVaultTenantName,
  reconcileVaultActiveTenantFromAuthMe,
  resolveOperativeRoleForActiveTenant,
  resolveTenantFromAuthMeAndMyTenants,
  resolveVaultActiveTenantId,
  shouldSendVaultXTenantHeader,
  type VaultTenantRoleAssignment,
} from './vault-active-tenant.util';
import {
  extractAuthMeTenantsArray,
  unwrapVaultApiArray,
  unwrapVaultApiRecord,
} from './vault-api-unwrap.util';
import { VaultAccountBlockedService } from './vault-account-blocked.service';
import {
  isVaultAccountBlockedCircuitOpen,
  resolveVaultAccountBlockedState,
} from './vault-account-blocked.util';
import {
  applyVaultSuperadminScope,
  persistVaultDashboardRoleFromStats,
  persistVaultOperativeRoleFromAuthMe,
  persistVaultUserIdFromAuthMe,
  readPersistedVaultUserId,
  syncVaultOperativeRoleFromTenantAssignments,
} from './vault-operative-role.util';
import {
  persistVaultTenantAssignmentsToStorage,
  readVaultTenantAssignmentsFromStorage,
} from './vault-tenant-assignments.storage';
import {
  firstModulePermissionTenantHint,
  persistVaultTenantCatalog,
  persistVaultTenantCatalogFromTenantRows,
  resolveVaultTenantIdByName,
  resolveVaultTenantNameFromCatalog,
} from './vault-tenant-catalog.util';
import { vaultCurrentUserIdFromSession, sessionJwtIndicatesVaultSuperadmin } from './vault-user-roles.util';
import {
  hydrateVaultTenantSnapshotToSession,
  persistVaultTenantSnapshot,
  reconcileVaultTenantSnapshotFromSession,
} from './vault-tenant-snapshot.util';
import {
  normalizeVaultTenantId,
  persistVaultTenantScopeToStorage,
  readVaultTenantIdFromAssignmentRow,
  resolveVaultActiveTenantIdFromTokenAndStorage,
  sanitizeInvalidVaultTenantScopeInStorage,
  syncVaultTenantScopeFromAccessToken,
  isVaultUuid,
} from './vault-tenant-scope.util';
import { syncVaultSuperadminInStoredUserData } from './vault-menu/vault-role-menu-matrix.util';
import { vaultSessionPinIsActive, applyVaultSessionPinToStorage } from './vault-session-pin.util';

type DashboardStatsPayload = { role?: string };

/**
 * Hidrata empresa Vault asignada al usuario (`user_tenants` / my-tenants / auth/me).
 * No usa la empresa Simappe del login (Nebula); esa capa es solo OAuth y menú.
 */
@Injectable({ providedIn: 'root' })
export class VaultTenantSessionBootstrapService {
  private readonly http = inject(HttpClient);
  private readonly accountBlocked = inject(VaultAccountBlockedService);

  ensureSession$(options?: { force?: boolean }): Observable<string> {
    if (typeof window === 'undefined') return of('');
    if (isVaultAccountBlockedCircuitOpen()) return of('');

    const token = localStorage.getItem('access_token');
    if (!token) return of('');

    sanitizeInvalidVaultTenantScopeInStorage();
    if (sessionJwtIndicatesVaultSuperadmin()) {
      applyVaultSuperadminScope();
    }
    if (!vaultSessionPinIsActive()) {
      this.restoreFromLocalAssignments();
    }
    this.restoreFromSnapshot();
    if (vaultSessionPinIsActive()) {
      applyVaultSessionPinToStorage();
    }

    const cached = resolveVaultActiveTenantId();
    const cachedName = cached
      ? resolveVaultTenantNameFromCatalog(cached) || readStoredVaultTenantName(cached)
      : '';
    // Tras login el tenant puede estar en caché pero aún sin vault_user_id de /auth/me.
    // Sin ese id, GET /assigned?user_id=… usa el UUID Simappe y deja la bóveda vacía hasta F5.
    const vaultUserId = readPersistedVaultUserId();
    if (cached && cachedName && vaultUserId && !options?.force) {
      this.finalize(cached);
      return of(cached);
    }

    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });

    return this.http.get<unknown>('/api/tenants/my-tenants', { headers }).pipe(
      catchError(() => of(null)),
      switchMap((myTenantsRaw) => {
        const myTenants = unwrapVaultApiArray(myTenantsRaw);
        if (!vaultSessionPinIsActive()) {
          this.ingestTenantRows(myTenants);
        }

        return this.http.get<unknown>('/api/auth/me', { headers }).pipe(
          catchError((err: HttpErrorResponse) => {
            if (resolveVaultAccountBlockedState(err)) {
              this.accountBlocked.applyFromError(err);
              return throwError(() => err);
            }
            return of(null);
          }),
          switchMap((meRaw) => {
            const me = unwrapVaultApiRecord(meRaw);
            if (meRaw) {
              persistVaultUserIdFromAuthMe(meRaw);
              if (!vaultSessionPinIsActive()) {
                const meTenants = extractAuthMeTenantsArray(meRaw);
                this.ingestTenantRows(meTenants);
              }
              persistVaultOperativeRoleFromAuthMe(meRaw);
              reconcileVaultActiveTenantFromAuthMe(meRaw);
            }

            let tenantId = normalizeVaultTenantId(resolveVaultActiveTenantId());
            if (!tenantId) {
              const resolved = normalizeVaultTenantId(
                resolveTenantFromAuthMeAndMyTenants(me, myTenants),
              );
              if (resolved) {
                persistVaultTenantScopeToStorage(resolved);
                tenantId = resolved;
              }
            }
            if (!tenantId) {
              syncVaultTenantScopeFromAccessToken();
              tenantId = normalizeVaultTenantId(resolveVaultActiveTenantId());
            }

            const loadStats$ = (scopedTenantId: string) => {
              const scoped = this.buildScopedRequest(token, scopedTenantId);
              return scopedTenantId
                ? this.http
                    .get<DashboardStatsPayload>('/api/dashboard/stats', {
                      headers: scoped.headers,
                      ...(scoped.params ? { params: scoped.params } : {}),
                    })
                    .pipe(catchError(() => of(null)))
                : this.http
                    .get<DashboardStatsPayload>('/api/dashboard/stats', {
                      headers: scoped.headers,
                    })
                    .pipe(catchError(() => of(null)));
            };

            if (tenantId) {
              return loadStats$(tenantId);
            }

            return this.resolveTenantFromModulePermissions$(token, me).pipe(
              switchMap(() => loadStats$(normalizeVaultTenantId(resolveVaultActiveTenantId()))),
            );
          }),
        );
      }),
      tap((stats) => {
        if (stats?.role) {
          persistVaultDashboardRoleFromStats(stats.role);
        }
        syncVaultSuperadminInStoredUserData();
        this.finalize(resolveVaultActiveTenantId());
      }),
      map(() => resolveVaultActiveTenantId()),
      catchError((err) => {
        if (err instanceof HttpErrorResponse && resolveVaultAccountBlockedState(err)) {
          this.accountBlocked.applyFromError(err);
          return throwError(() => err);
        }
        this.finalize(resolveVaultActiveTenantId());
        return of(resolveVaultActiveTenantId());
      }),
    );
  }

  private restoreFromSnapshot(): void {
    const snapshotTenantId = hydrateVaultTenantSnapshotToSession(
      (rows) => persistVaultAuthMeTenants(rows),
      (rows) => persistVaultTenantCatalogFromTenantRows(rows),
    );
    if (snapshotTenantId && !resolveVaultActiveTenantIdFromTokenAndStorage()) {
      persistVaultTenantScopeToStorage(snapshotTenantId);
    }
  }

  private restoreFromLocalAssignments(): void {
    const assignments = readVaultTenantAssignmentsFromStorage();
    if (!assignments.length) return;
    persistVaultAuthMeTenants(assignments);
    const withId = assignments.filter((a) => normalizeVaultTenantId(a.tenant_id));
    if (withId.length) {
      persistVaultTenantFromTenantsPayload(withId);
    }
  }

  private ingestTenantRows(rows: unknown[]): void {
    if (!rows.length) return;

    const assignments = rows
      .map((entry) => this.normalizeAssignmentRow(entry))
      .filter((row): row is VaultTenantRoleAssignment => !!row);

    if (!assignments.length) return;

    persistVaultAuthMeTenants(assignments);
    persistVaultTenantCatalogFromTenantRows(assignments);
    persistVaultTenantAssignmentsToStorage(assignments);
    persistVaultTenantFromTenantsPayload(assignments);
    syncVaultOperativeRoleFromTenantAssignments();
  }

  private normalizeAssignmentRow(entry: unknown): VaultTenantRoleAssignment | null {
    if (!entry || typeof entry !== 'object') return null;
    const row = entry as Record<string, unknown>;

    let tenant_id =
      normalizeVaultTenantId(String(row['tenant_id'] ?? row['tenantId'] ?? '')) ||
      readVaultTenantIdFromAssignmentRow(row);
    let tenant_name = String(row['tenant_name'] ?? row['tenantName'] ?? '').trim();

    const nested = row['tenant'];
    if (nested && typeof nested === 'object') {
      const nestedRow = nested as Record<string, unknown>;
      tenant_id = tenant_id || readVaultTenantIdFromAssignmentRow(nestedRow);
      if (!tenant_name) {
        tenant_name = String(nestedRow['name'] ?? nestedRow['tenant_name'] ?? '').trim();
      }
    }

    if (!tenant_id && tenant_name) {
      tenant_id = resolveVaultTenantIdByName(tenant_name);
    }

    const role = String(row['role'] ?? '').trim();
    if (!tenant_id && !tenant_name && !role) return null;

    return {
      tenant_id: normalizeVaultTenantId(tenant_id) || tenant_id,
      tenant_name: tenant_name || undefined,
      role,
    };
  }

  /** Fallback cuando `my-tenants` viene vacío pero el usuario tiene permisos en un tenant. */
  private resolveTenantFromModulePermissions$(
    token: string,
    me: Record<string, unknown> | null,
  ): Observable<void> {
    const userId = String(me?.['id'] ?? vaultCurrentUserIdFromSession() ?? '').trim();
    if (!userId || !isVaultUuid(userId)) return of(undefined);

    const { headers } = this.buildScopedRequest(token, '');
    return this.http
      .get<unknown>(`/api/module-permissions/user/${encodeURIComponent(userId)}`, { headers })
      .pipe(
        tap((raw) => {
          const hint = firstModulePermissionTenantHint(raw);
          const tid = normalizeVaultTenantId(hint?.tenant_id);
          if (!tid) return;
          persistVaultTenantScopeToStorage(tid);
          const role = resolveOperativeRoleForActiveTenant(tid) || '';
          const row: VaultTenantRoleAssignment = {
            tenant_id: tid,
            tenant_name: hint?.tenant_name || undefined,
            role,
          };
          persistVaultAuthMeTenants([row]);
          persistVaultTenantAssignmentsToStorage([row]);
          persistVaultTenantFromTenantsPayload([row]);
          if (hint?.tenant_name) {
            persistVaultTenantCatalog([{ id: tid, name: hint.tenant_name }]);
          }
          persistVaultTenantSnapshot({
            tenant_id: tid,
            tenant_name: hint?.tenant_name,
            role,
          });
        }),
        map(() => undefined),
        catchError(() => of(undefined)),
      );
  }

  private buildScopedRequest(
    token: string,
    tenantId: string,
  ): { headers: HttpHeaders; params?: HttpParams } {
    let headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const id = normalizeVaultTenantId(tenantId);
    if (!id || !shouldSendVaultXTenantHeader()) return { headers };
    headers = headers.set('X-Tenant-ID', id);
    return { headers, params: new HttpParams().set('tenant_id', id) };
  }

  private finalize(tenantId: string): void {
    if (vaultSessionPinIsActive()) {
      applyVaultSessionPinToStorage();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('vault-session-updated'));
      }
      return;
    }

    reconcileVaultTenantSnapshotFromSession(
      (rows) => persistVaultAuthMeTenants(rows),
      (rows) => persistVaultTenantCatalogFromTenantRows(rows),
    );

    const resolved = normalizeVaultTenantId(tenantId || resolveVaultActiveTenantId());
    if (resolved) {
      const name =
        resolveVaultTenantNameFromCatalog(resolved) || readStoredVaultTenantName(resolved);
      const role = resolveOperativeRoleForActiveTenant(resolved) || '';
      persistVaultTenantSnapshot({ tenant_id: resolved, tenant_name: name, role });
      if (role) syncVaultOperativeRoleFromTenantAssignments(resolved);
      persistVaultTenantAssignmentsToStorage([
        { tenant_id: resolved, tenant_name: name || undefined, role },
      ]);
    }

    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('vault-session-updated'));
    }
  }
}
